import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { SelectDatePicker } from '../../../../components/Forms/datePicker';
import { InputText } from '../../../../components/Forms/InputText';
import { SelectInput } from '../../../../components/Forms/selectInput';
import { useCustomStore } from '../../../../hooks';
import type { MotorClubStateOption } from '../../../../store/api/motorClubMembership.api';

export interface TrafficIncidentDriverOption {
  driverId: string;
  driverName: string;
}

export interface AddTrafficIncidentPayload {
  ackAttorney: boolean;
  ackMvr: boolean;
  dateIssued: string;
  documents: File[];
  driverId: string;
  driverName: string;
  stateIssued: string;
  statement: string;
}

interface AddTrafficIncidentModalProps {
  driverOptions: TrafficIncidentDriverOption[];
  isLoadingDrivers?: boolean;
  showDriverView?: boolean;
  onClose: () => void;
  onSubmit: (payload: AddTrafficIncidentPayload) => Promise<boolean>;
  open: boolean;
}

type IncidentStep = 'form' | 'done';

// Excluded Hawaii, Alaska states for motor club traffic incidents
const nowAllowedStates = [
  'AK',
  'HI',
  'f05ddef5-a08d-eb11-a9f6-005056a93d01',
  'fa5ddef5-a08d-eb11-a9f6-005056a93d01',
];

const AddTrafficIncidentModal: React.FC<AddTrafficIncidentModalProps> = ({
  driverOptions,
  showDriverView = false,
  isLoadingDrivers = false,
  onClose,
  onSubmit,
  open,
}) => {
  const { authStore, motorClubMembershipStore } = useCustomStore();
  const [step, setStep] = React.useState<IncidentStep>('form');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoadingStates, setIsLoadingStates] = React.useState(false);
  const [motorClubStates, setMotorClubStates] = React.useState<
    MotorClubStateOption[]
  >([]);

  const [driverId, setDriverId] = React.useState('');
  const [stateIssued, setStateIssued] = React.useState('');
  const [dateIssued, setDateIssued] = React.useState('');
  const [statement, setStatement] = React.useState('');
  const [ackMvr, setAckMvr] = React.useState(false);
  const [ackAttorney, setAckAttorney] = React.useState(false);
  const [documents, setDocuments] = React.useState<File[]>([]);

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = React.useState(false);

  const selectedDriver = React.useMemo(
    () => driverOptions.find((option) => option.driverId === driverId),
    [driverId, driverOptions],
  );

  const driverSelectOptions = React.useMemo(
    () =>
      driverOptions.map((option) => ({
        label: option.driverName,
        value: option.driverId,
      })),
    [driverOptions],
  );

  const stateIssuedOptions = React.useMemo(() => {
    return motorClubStates
      .filter((state) => nowAllowedStates.indexOf(state.id) === -1)
      .map((state) => ({
        label: state.fullName || '',
        value: state.id || '',
      }));
  }, [motorClubStates]);

  const validate = React.useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!showDriverView && !driverId.trim()) {
      nextErrors.driverId = 'Required';
    }
    if (!stateIssued.trim()) {
      nextErrors.stateIssued = 'Required';
    }
    if (!dateIssued.trim()) {
      nextErrors.dateIssued = 'Required';
    }
    if (!statement.trim()) {
      nextErrors.statement = 'Required';
    }
    if (!documents.length) {
      nextErrors.documents = 'Please upload at least one document';
    }
    if (!ackMvr) {
      nextErrors.ackMvr = 'Required';
    }
    if (!ackAttorney) {
      nextErrors.ackAttorney = 'Required';
    }

    setErrors(nextErrors);
    return nextErrors;
  }, [
    ackAttorney,
    ackMvr,
    dateIssued,
    documents.length,
    driverId,
    showDriverView,
    stateIssued,
    statement,
  ]);

  React.useEffect(() => {
    if (showErrors) {
      validate();
    }
  }, [showErrors, validate]);

  const resetAndClose = React.useCallback(() => {
    setStep('form');
    setIsSubmitting(false);
    setDriverId('');
    setStateIssued('');
    setDateIssued('');
    setStatement('');
    setAckMvr(false);
    setAckAttorney(false);
    setDocuments([]);
    setErrors({});
    setShowErrors(false);
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    if (!open) {
      setStep('form');
      setIsSubmitting(false);
      setDriverId('');
      setStateIssued('');
      setDateIssued('');
      setStatement('');
      setAckMvr(false);
      setAckAttorney(false);
      setDocuments([]);
      setErrors({});
      setShowErrors(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    let isMounted = true;
    if (motorClubStates.length) {
      return;
    }

    setIsLoadingStates(true);
    motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore
      .getMotorClubStates()
      .then((responseOrErr) => {
        if (!isMounted) {
          return;
        }

        if (responseOrErr.isOk()) {
          setMotorClubStates(responseOrErr.value);
        }
        setIsLoadingStates(false);
      })
      .catch(() => {
        if (isMounted) {
          setIsLoadingStates(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [motorClubMembershipStore, motorClubStates.length, open]);

  const handleDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    setDocuments(selectedFiles);
  };

  const formatDateForPayload = (value: unknown) => {
    if (!value) {
      return '';
    }

    const parsed = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const year = parsed.getFullYear();
    const monthNumber = parsed.getMonth() + 1;
    const dayNumber = parsed.getDate();
    const month = monthNumber < 10 ? `0${monthNumber}` : `${monthNumber}`;
    const day = dayNumber < 10 ? `0${dayNumber}` : `${dayNumber}`;
    return `${year}-${month}-${day}`;
  };

  const submitIncident = async () => {
    setShowErrors(true);
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      return;
    }

    setIsSubmitting(true);
    let isSuccess = false;
    try {
      isSuccess = await onSubmit({
        ackAttorney,
        ackMvr,
        dateIssued,
        documents,
        driverId: showDriverView
          ? authStore.getTokenDetail?.id || ''
          : driverId,
        driverName: selectedDriver?.driverName || '',
        stateIssued: stateIssued.trim(),
        statement: statement.trim(),
      });
    } finally {
      setIsSubmitting(false);
    }

    if (isSuccess) {
      setStep('done');
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={resetAndClose}>
      <DialogTitle sx={{ padding: '16px 22px 10px 22px' }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Typography
            sx={{
              color: '#1A1A1A',
              fontFamily: 'FiraSans-SemiBold',
              fontSize: '20px',
            }}>
            Submit a Traffic Incident
          </Typography>
          <Button onClick={resetAndClose} sx={closeButtonSx}>
            x
          </Button>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ padding: '18px 22px' }}>
        {step === 'form' ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            {!showDriverView && (
              <Box>
                <Typography sx={fieldLabelSx}>Driver *</Typography>
                <SelectInput
                  name="incidentDriver"
                  value={driverId}
                  onChangeText={(value) => setDriverId(value)}
                  optionsList={driverSelectOptions}
                  size="small"
                  placeHolder="Select a driver"
                  defaultOptionLabel="Select a driver"
                  errors={showErrors ? errors.driverId || '' : ''}
                  isEditable={false}
                />
                {isLoadingDrivers ? (
                  <Typography sx={helperTextSx}>Loading drivers...</Typography>
                ) : null}
              </Box>
            )}

            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' },
              }}>
              <Box>
                <Typography sx={fieldLabelSx}>State Issued *</Typography>
                <SelectInput
                  name="incidentStateIssued"
                  value={stateIssued}
                  onChangeText={(value) => setStateIssued(value)}
                  optionsList={stateIssuedOptions}
                  size="small"
                  placeHolder="Select a state"
                  defaultOptionLabel="Select a state"
                  errors={showErrors ? errors.stateIssued || '' : ''}
                  isEditable={false}
                />
                {isLoadingStates ? (
                  <Typography sx={helperTextSx}>Loading states...</Typography>
                ) : null}
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Incident Date *</Typography>
                <SelectDatePicker
                  name="incidentDateIssued"
                  value={dateIssued}
                  onChangeDate={(value) => {
                    setDateIssued(formatDateForPayload(value));
                  }}
                  errors={showErrors ? errors.dateIssued || '' : ''}
                  isFormSubmittedOnce={showErrors}
                  isRefreshValue={true}
                  maxDate={new Date()}
                  disabled={false}
                />
              </Box>
            </Box>

            <Box>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'inline-flex',
                  gap: '6px',
                }}>
                <Typography sx={fieldLabelSx}>Incident Document *</Typography>
                <Tooltip
                  arrow
                  title="Upload the document that matches your incident type. You can upload multiple files.">
                  <Typography sx={tooltipIconSx}>?</Typography>
                </Tooltip>
              </Box>
              <TextField
                type="file"
                inputProps={{ multiple: true }}
                onChange={handleDocumentChange}
                size="small"
                sx={fileInputSx}
                fullWidth
              />
              {showErrors && errors.documents ? (
                <Typography sx={fieldErrorSx}>{errors.documents}</Typography>
              ) : null}
            </Box>

            <Box>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'inline-flex',
                  gap: '6px',
                }}>
                <Typography sx={fieldLabelSx}>Driver Statement *</Typography>
                <Tooltip
                  arrow
                  title="The law firm requires a statement from the driver to review the incident and prepare the defense. Please describe what happened in the driver’s own words and include all relevant details.">
                  <Typography sx={tooltipIconSx}>?</Typography>
                </Tooltip>
              </Box>
              <InputText
                name="incidentDriverStatement"
                value={statement}
                onChangeText={(value) => setStatement(value)}
                placeholder="Please document the incident."
                errors={showErrors ? errors.statement || '' : ''}
                isFormSubmittedOnce={showErrors}
                isRefreshValue={true}
                isEditable={false}
                type="text"
                isTextArea={true}
              />
            </Box>

            <Box sx={ackCardSx}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ackMvr}
                    onChange={(event) => setAckMvr(event.target.checked)}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Typography sx={ackTextSx}>
                    * By selecting this, you acknowledge that you understand an
                    MVR will need to be pulled and paid for by you.
                  </Typography>
                }
              />
              {showErrors && errors.ackMvr ? (
                <Typography sx={fieldErrorSx}>{errors.ackMvr}</Typography>
              ) : null}
            </Box>

            <Box sx={ackCardSx}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ackAttorney}
                    onChange={(event) => setAckAttorney(event.target.checked)}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Typography sx={ackTextSx}>
                    * By selecting this, you acknowledge that you understand and
                    accept the attorney-client waiver.
                  </Typography>
                }
              />
              {showErrors && errors.ackAttorney ? (
                <Typography sx={fieldErrorSx}>{errors.ackAttorney}</Typography>
              ) : null}
            </Box>
          </Box>
        ) : null}

        {step === 'done' ? (
          <Box sx={{ padding: '12px 0 6px', textAlign: 'center' }}>
            <Typography
              sx={{
                color: '#2F8F4E',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '34px',
                lineHeight: 1,
                marginBottom: '8px',
              }}>
              ✓
            </Typography>
            <Typography
              sx={{
                color: '#1A1A1A',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '16px',
                marginBottom: '6px',
              }}>
              Great! Your incident has been submitted!
            </Typography>
            <Typography
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-Regular',
                fontSize: '13px',
                lineHeight: 1.55,
              }}>
              Our team will contact you within one business day.
              <br />
              Thank you for being a valued Motor Club member!
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ backgroundColor: '#F7F7F8', padding: '14px 22px' }}>
        {step === 'form' ? (
          <>
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={submitIncident}
              disabled={isSubmitting}
              sx={primaryButtonSx}
              variant="contained">
              {isSubmitting ? (
                <CircularProgress size={18} sx={{ color: '#FFFFFF' }} />
              ) : (
                'Submit Incident'
              )}
            </Button>
          </>
        ) : null}

        {step === 'done' ? (
          <Button
            onClick={resetAndClose}
            sx={{ ...primaryButtonSx, width: '100%' }}
            variant="contained">
            Back to Dashboard
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

const fieldLabelSx = {
  color: '#1A1A1A',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '12px',
  marginBottom: '6px',
};

const helperTextSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Regular',
  fontSize: '11.5px',
  marginTop: '4px',
};

const fileInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
  },
  '& input[type="file"]': {
    fontFamily: 'FiraSans-Regular',
    fontSize: '12px',
    padding: '8px 10px',
  },
};

const ackCardSx = {
  border: '1px solid #E2E2E6',
  borderRadius: '10px',
  padding: '8px 10px',
};

const checkboxSx = {
  '&.Mui-checked': {
    color: '#DEC330',
  },
  color: '#DEC330',
};

const ackTextSx = {
  color: '#2B2B2B',
  fontFamily: 'FiraSans-Regular',
  fontSize: '12.5px',
  lineHeight: 1.5,
};

const tooltipIconSx = {
  alignItems: 'center',
  backgroundColor: '#F1F1F4',
  border: '1px solid #D8D8DD',
  borderRadius: '50%',
  color: '#66666F',
  cursor: 'help',
  display: 'inline-flex',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '10px',
  height: '16px',
  justifyContent: 'center',
  lineHeight: 1,
  width: '16px',
};

const fieldErrorSx = {
  color: '#d32f2f',
  fontFamily: 'FiraSans-Regular',
  fontSize: '12px',
  marginTop: '6px',
};

const closeButtonSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-SemiBold',
  minWidth: '32px',
  padding: 0,
  textTransform: 'none',
};

const cancelButtonSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-SemiBold',
  textTransform: 'none',
};

const primaryButtonSx = {
  '&:hover': {
    backgroundColor: '#CBB12B',
  },
  backgroundColor: '#DEC330',
  borderRadius: '8px',
  color: '#241A2E',
  fontFamily: 'FiraSans-SemiBold',
  minWidth: '140px',
  textTransform: 'none',
};

export default AddTrafficIncidentModal;
